import React, { useState } from "react";
import AdminSidebar from "./Sidebar/AdminSidebar";
import AdminHeader from "./Header/AdminHeader";
import admin_imgUpld from "../../assets/admin/admin_imgUpld.webp";
import { MdOutlineFileUpload } from "react-icons/md";
import { MdDateRange } from "react-icons/md";
import { toast } from "react-toastify";
import { TiTick } from "react-icons/ti";
import { env } from "../../core/Service/envconfig";
import { LuLoaderCircle } from "react-icons/lu";
import RealTFactoryAbi from "./abi/RealTFactoryAbi.json";
import { getMethod, postMethod } from "../../core/service/common.api";
import apiService from "../../core/Service/detail";

import { ethers } from "ethers";
import { Contract, Interface } from "ethers"; // updated for v6
const FACTORY_ADDRESS = "0xEe57DEe766eaDc979F65070c80a8c05b0707F598";

const fundTermOptions = [
  "Select Term",
  "Open ended",
  "3 - Months",
  "6 - Months",
  "9 - Months",
  "1 - Year",
  "Custom",
];

const lockOption = [
  "Open ended",
  "3 - Months",
  "6 - Months",
  "9 - Months",
  "1 - Year",
  "Custom",
];

const assetTypeOptions = [
  "Real Estate",
  "Treasuries & Bonds",
  "Private Credit",
  "Commodities",
  "Equities",
  "Stocks",
];

const Tokenization = () => {
  // Step management
  const stepCount = [
    { step: 1, label: "General Details" },
    { step: 2, label: "Asset-Specific Details" },
    { step: 3, label: "Documents" },
    { step: 4, label: "Staking Details" },
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Main form state
  const [formData, setFormData] = useState({
    // Step 1: General Details
    profileImage: "",
    fundName: "",
    description: "",
    assetType: "",
    assetClass: "",
    fundTerm: "",
    minInvestment: "",
    tokenPrice: "",
    availableTokens: "",
    targetReturns: "",
    geographicFocus: "",
    investmentStrategy: "",

    // Step 2: Asset-Specific Details (dynamic based on assetType)
    propertyType: "",
    yearBuilt: "",
    floors: "",
    totalArea: "",
    leaseExpiry: "",
    issuer: "",
    creditRating: "",
    interestRate: "",
    maturityDate: "",
    repaymentSchedule: "",
    collateralDetails: "",
    commodityType: "",
    unitOfMeasure: "",
    storageLocation: "",
    storageCost: "",
    companyName: "",
    tickerSymbol: "",
    exchange: "",
    sharePrice: "",
    dividendYield: "",
    marketCap: "",
    sector: "",

    // Step 3: Documents
    whitepaperDoc: "",
    valuationDoc: "",
    legalDoc: "",

    // Step 4: Staking
    lockPeriod: "",
    apy: "",
    minStake: "",
    isReviewed: false,
    isConfirmed: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [uploadStatus, setUploadStatus] = useState({
    whitepaper: false,
    valuation: false,
    legal: false,
    profile: false,
  });

  // Toast functions
  const showToast = {
    success: (message) => toast.success(message),
    error: (message) => toast.error(message),
    dismiss: () => toast.dismiss(),
  };

  // Navigation handlers
  const handleBack = (e) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (await validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    setFormData({
      profileImage: "",
      fundName: "",
      description: "",
      assetType: "",
      assetClass: "",
      fundTerm: "",
      minInvestment: "",
      tokenPrice: "",
      availableTokens: "",
      targetReturns: "",
      geographicFocus: "",
      investmentStrategy: "",
      propertyType: "",
      yearBuilt: "",
      floors: "",
      totalArea: "",
      leaseExpiry: "",
      issuer: "",
      creditRating: "",
      interestRate: "",
      maturityDate: "",
      repaymentSchedule: "",
      collateralDetails: "",
      commodityType: "",
      unitOfMeasure: "",
      storageLocation: "",
      storageCost: "",
      companyName: "",
      tickerSymbol: "",
      exchange: "",
      sharePrice: "",
      dividendYield: "",
      marketCap: "",
      sector: "",
      whitepaperDoc: "",
      valuationDoc: "",
      legalDoc: "",
      lockPeriod: "",
      apy: "",
      minStake: "",
      isReviewed: false,
      isConfirmed: false,
    });
    setErrors({});
    setTouched({});
    setUploadStatus({
      whitepaper: false,
      valuation: false,
      legal: false,
      profile: false,
    });
    setCurrentStep(1);
    // showToast.success("Form cleared successfully");
  };

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.profileImage)
      newErrors.profileImage = "Profile image is required";
    if (!formData.fundName?.trim())
      newErrors.fundName = "Asset name is required";
    if (!formData.assetType) newErrors.assetType = "Asset type is required";
    if (!formData.minInvestment || formData.minInvestment <= 0)
      newErrors.minInvestment = "Valid minimum investment is required";
    if (formData.tokenPrice && formData.tokenPrice <= 0)
      newErrors.tokenPrice = "Token price must be positive";
    if (formData.availableTokens && formData.availableTokens <= 0)
      newErrors.availableTokens = "Available tokens must be positive";
    if (
      formData.targetReturns &&
      !/^\d+(\.\d+)?%?\s*(p\.a)?$/i.test(formData.targetReturns)
    )
      newErrors.targetReturns = "Enter valid target returns (e.g., 10% p.a)";

    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    const { assetType } = formData;

    if (assetType === "Real Estate") {
      if (!formData.propertyType?.trim())
        newErrors.propertyType = "Property type is required";
      if (
        !formData.yearBuilt ||
        formData.yearBuilt < 1800 ||
        formData.yearBuilt > new Date().getFullYear()
      )
        newErrors.yearBuilt = "Valid year built is required";
    }

    if (assetType === "Treasuries & Bonds" || assetType === "Private Credit") {
      if (!formData.issuer?.trim()) newErrors.issuer = "Issuer is required";
      if (!formData.interestRate?.trim())
        newErrors.interestRate = "Interest rate is required";
    }

    if (assetType === "Equities") {
      if (!formData.companyName?.trim())
        newErrors.companyName = "Company name is required";
      if (!formData.tickerSymbol?.trim())
        newErrors.tickerSymbol = "Ticker symbol is required";
    }

    if (assetType === "Stocks") {
      if (!formData.companyName?.trim())
        newErrors.companyName = "Company name is required";
      if (!formData.tickerSymbol?.trim())
        newErrors.tickerSymbol = "Ticker symbol is required";
    }

    return newErrors;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.whitepaperDoc)
      newErrors.whitepaperDoc = "Whitepaper is required";
    if (!formData.valuationDoc)
      newErrors.valuationDoc = "Valuation report is required";
    if (!formData.legalDoc)
      newErrors.legalDoc = "Legal disclosures are required";

    return newErrors;
  };

  const validateStep4 = () => {
    const newErrors = {};

    if (!formData.lockPeriod) newErrors.lockPeriod = "Lock period is required";
    if (!formData.apy?.trim()) newErrors.apy = "APY is required";
    // else if (!/^\d+(\.\d+)?%?\s*p\.a$/i.test(formData.apy.trim()))
    //   newErrors.apy = "Enter valid APY (e.g., 10.5% p.a)";

    if (!formData.minStake || formData.minStake <= 0)
      newErrors.minStake = "Valid minimum stake is required";
    if (!formData.isReviewed)
      newErrors.isReviewed = "Review confirmation is required";
    if (!formData.isConfirmed)
      newErrors.isConfirmed = "Accuracy confirmation is required";

    return newErrors;
  };

  const validateCurrentStep = () => {
    let newErrors = {};

    switch (currentStep) {
      case 1:
        newErrors = validateStep1();
        break;
      case 2:
        newErrors = validateStep2();
        break;
      case 3:
        newErrors = validateStep3();
        break;
      case 4:
        newErrors = validateStep4();
        break;
      default:
        break;
    }

    setErrors(newErrors);

    // Mark all fields as touched for current step
    const stepFields = getStepFields(currentStep);
    const newTouched = { ...touched };
    stepFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    if (Object.keys(newErrors).length === 0) {
      return true;
    } else {
      showToast.error("Please fix the errors before proceeding");
      return false;
    }
  };

  const getStepFields = (step) => {
    switch (step) {
      case 1:
        return [
          "profileImage",
          "fundName",
          "assetType",
          "minInvestment",
          "tokenPrice",
          "availableTokens",
          "targetReturns",
        ];
      case 2:
        const assetType = formData.assetType;
        const baseFields = [
          "propertyType",
          "yearBuilt",
          "issuer",
          "interestRate",
          "companyName",
          "tickerSymbol",
        ];
        return baseFields.filter((field) => formData[field] !== undefined);
      case 3:
        return ["whitepaperDoc", "valuationDoc", "legalDoc"];
      case 4:
        return ["lockPeriod", "apy", "minStake", "isReviewed", "isConfirmed"];
      default:
        return [];
    }
  };

  // Form field handlers
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Mark as touched
    if (!touched[field]) {
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    }
  };

  const handleAssetType = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      assetType: selectedOption,
    }));

    if (errors.assetType) {
      setErrors((prev) => ({
        ...prev,
        assetType: "",
      }));
    }
  };

  const handleFundTerm = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      fundTerm: selectedOption,
    }));
  };

  // File upload function
  const uploadFile = async (file, type) => {
    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const fileSize = file.size;
      const maxSize = 5 * 1024 * 1024; // 5MB

      // Validate file type based on upload type
      if (type === "PROFILE") {
        if (!["png", "webp", "jpeg", "jpg"].includes(fileExtension)) {
          throw new Error("Profile image must be PNG, WebP, JPEG, or JPG");
        }
      } else {
        if (!["pdf", "doc", "docx"].includes(fileExtension)) {
          throw new Error("Document must be PDF, DOC, or DOCX");
        }
      }

      if (fileSize > maxSize) {
        throw new Error("File size must be less than 5MB");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", env.upload_preset);
      formData.append("cloud_name", env.cloud_name);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${env.cloud_name}/auto/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      throw error;
    }
  };

  const handleProfileImageUpload = async (file) => {
    try {
      setUploadStatus((prev) => ({ ...prev, profile: true }));
      const imageUrl = await uploadFile(file, "PROFILE");

      setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
      setUploadStatus((prev) => ({ ...prev, profile: false }));

      if (errors.profileImage) {
        setErrors((prev) => ({ ...prev, profileImage: "" }));
      }

      showToast.success("Profile image uploaded successfully");
    } catch (error) {
      setUploadStatus((prev) => ({ ...prev, profile: false }));
      showToast.error(error.message);
    }
  };

  const handleDocumentUpload = async (file, docType) => {
    try {
      setUploadStatus((prev) => ({ ...prev, [docType.toLowerCase()]: true }));
      const docUrl = await uploadFile(file, "DOCUMENT");

      const fieldMap = {
        whitepaper: "whitepaperDoc",
        valuation: "valuationDoc",
        legal: "legalDoc",
      };

      setFormData((prev) => ({ ...prev, [fieldMap[docType]]: docUrl }));
      setUploadStatus((prev) => ({ ...prev, [docType.toLowerCase()]: false }));

      if (errors[fieldMap[docType]]) {
        setErrors((prev) => ({ ...prev, [fieldMap[docType]]: "" }));
      }

      showToast.success(`${docType} uploaded successfully`);
    } catch (error) {
      setUploadStatus((prev) => ({ ...prev, [docType.toLowerCase()]: false }));
      showToast.error(error.message);
    }
  };

  // Final submission

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all steps before submission
    if (!(await validateCurrentStep())) {
      showToast.error("Please fix all errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create token on blockchain
      let blockchainResult;
      try {
        blockchainResult = await createTokenOnBlockchain(formData);
        showToast.success("Token created successfully on blockchain!");
      } catch (blockchainError) {
        console.error("Blockchain error:", blockchainError);
        showToast.error(`Blockchain error: ${blockchainError.message}`);
        throw new Error(
          `Blockchain creation failed: ${blockchainError.message}`
        );
      }

      // Step 2: Prepare data for backend API
      const tokenData = {
        // General details
        assetName: formData.fundName,
        description: formData.description,
        assetType: formData.assetType,
        assetClass: formData.assetClass,
        fundTerm: formData.fundTerm,
        minInvestment: parseFloat(formData.minInvestment),
        tokenPrice: formData.tokenPrice
          ? parseFloat(formData.tokenPrice)
          : null,
        availableTokens: formData.availableTokens
          ? parseInt(formData.availableTokens)
          : null,
        targetReturns: formData.targetReturns,
        geographicFocus: formData.geographicFocus,
        investmentStrategy: formData.investmentStrategy,
        profileImage: formData.profileImage,

        // Asset-specific details
        assetDetails: getAssetSpecificDetails(),

        // Documents
        documents: {
          whitepaper: formData.whitepaperDoc,
          valuation: formData.valuationDoc,
          legal: formData.legalDoc,
        },

        // Staking details
        staking: {
          lockPeriod: formData.lockPeriod,
          apy: formData.apy,
          minStake: parseFloat(formData.minStake),
        },

        // Blockchain addresses
        TokenAddress: blockchainResult.tokenAddress,
        SaleAddress: blockchainResult.saleAddress,
        DistributeAddress: blockchainResult.distributeAddress,
        HashAddress: blockchainResult.hashAddress,

        // Metadata
        createdAt: new Date().toISOString(),
        status: "pending",
        isReviewed: formData.isReviewed,
        isConfirmed: formData.isConfirmed,
      };

      console.log("Submitting to backend:", tokenData);

      // Step 3: Send to backend API
      const apiData = {
        apiUrl: apiService.createToken, // Make sure apiService is imported/defined
        payload: tokenData,
      };

      const response = await postMethod(apiData); // Make sure postMethod is defined

      if (response.status === true) {
        showToast.success(response.message || "Asset created successfully!");

        // Reset form after successful submission
        handleClear(e);

        // Optional: Reset blockchain addresses
        setTransactionHash("");
        setTokenAddress("");
        setSaleAddress("");
        setDistributeAddress("");
      } else {
        throw new Error(
          response.message || "Failed to create asset in database"
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      showToast.error(
        error.message || "Failed to create asset. Please try again."
      );

      // Don't clear form on error - let user retry
    } finally {
      setIsSubmitting(false);
    }
  };

  // Updated blockchain token creation function
  const createTokenOnBlockchain = async () => {
    try {
      const PRIVATE_KEY =
        "531e90775edcf045d7ebae7fdb9d2f769d860721e7f00ba8ec4b753c3e57b480";
      const RPC_URL = "https://data-seed-prebsc-1-s1.binance.org:8545/";

      if (!PRIVATE_KEY) {
        throw new Error("Private key not configured");
      }

      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const signer = new ethers.Wallet(PRIVATE_KEY, provider);

      const factory = new Contract(FACTORY_ADDRESS, RealTFactoryAbi, signer);

      // Get existing properties count
      const existingProperties = await factory.getProperties();
      const existingCount = existingProperties.length;

      // Use form data for token creation (dynamic values)
      const tokenName = formData.fundName || "Asset Token";
      const tokenSymbol = generateTokenSymbol(formData.fundName);
      const tokenImage = formData.profileImage
        ? extractIpfsHash(formData.profileImage)
        : "QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco";

      // Calculate valuation based on form data
      const valuation =
        formData.minInvestment && formData.availableTokens
          ? ethers.parseUnits(
              (formData.minInvestment * formData.availableTokens).toString(),
              18
            )
          : ethers.parseUnits("1000000", 18);

      const totalSupply = formData.availableTokens
        ? ethers.parseUnits(formData.availableTokens.toString(), 18)
        : ethers.parseUnits("1000000", 18);

      const tokenPrice = formData.tokenPrice
        ? ethers.parseUnits(formData.tokenPrice.toString(), 18)
        : ethers.parseUnits("1", 18);

      // Default location or use from form data if available
      const location = formData.geographicFocus || "40.7128,-74.0060";

      const tx = await factory.createProperty(
        tokenName,
        tokenSymbol,
        tokenImage,
        location,
        valuation.toString(),
        totalSupply.toString(),
        tokenPrice.toString()
      );

      console.log("⏳ Transaction sent:", tx.hash);
      setTransactionHash(tx.hash);

      // Wait for transaction confirmation
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmed:", receipt.transactionHash);

      // Verify property was created
      const updatedProperties = await factory.getProperties();

      if (updatedProperties.length <= existingCount) {
        throw new Error("Transaction confirmed but no new property found");
      }

      const newProperty = updatedProperties[updatedProperties.length - 1];

      // Update state
      setTokenAddress(newProperty.token);
      setSaleAddress(newProperty.sale);
      setDistributeAddress(newProperty.distributor);

      return {
        tokenAddress: newProperty.token,
        saleAddress: newProperty.sale,
        distributeAddress: newProperty.distributor,
        hashAddress: tx.hash,
      };
    } catch (error) {
      console.error("❌ Blockchain error:", error);
      const errorMsg =
        error?.reason || error?.message || "Unknown blockchain error occurred";
      throw new Error(errorMsg);
    }
  };

  // Helper functions
  const generateTokenSymbol = (assetName) => {
    if (!assetName) return "AST";
    return assetName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("")
      .slice(0, 6);
  };

  const extractIpfsHash = (url) => {
    if (!url) return "QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco";
    const match = url.match(/ipfs:\/\/([a-zA-Z0-9]+)/);
    return match ? match[1] : "QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco";
  };

  // Make sure these are defined in your component
  const [transactionHash, setTransactionHash] = useState("");
  const [tokenAddress, setTokenAddress] = useState("");
  const [saleAddress, setSaleAddress] = useState("");
  const [distributeAddress, setDistributeAddress] = useState("");

  // Update your JSX to show blockchain status
  {
    transactionHash && (
      <div className="blockchain-status">
        <p>Transaction Hash: {transactionHash}</p>
        {tokenAddress && <p>Token Address: {tokenAddress}</p>}
      </div>
    );
  }

  const getAssetSpecificDetails = () => {
    const { assetType } = formData;
    const details = { assetType };

    switch (assetType) {
      case "Real Estate":
        details.propertyType = formData.propertyType;
        details.yearBuilt = formData.yearBuilt;
        details.floors = formData.floors;
        details.totalArea = formData.totalArea;
        details.leaseExpiry = formData.leaseExpiry;
        break;
      case "Treasuries & Bonds":
      case "Private Credit":
        details.issuer = formData.issuer;
        details.creditRating = formData.creditRating;
        details.interestRate = formData.interestRate;
        details.maturityDate = formData.maturityDate;
        if (assetType === "Private Credit") {
          details.repaymentSchedule = formData.repaymentSchedule;
          details.collateralDetails = formData.collateralDetails;
        }
        break;
      case "Commodities":
        details.commodityType = formData.commodityType;
        details.unitOfMeasure = formData.unitOfMeasure;
        details.storageLocation = formData.storageLocation;
        details.storageCost = formData.storageCost;
        break;
      case "Equities":
        details.companyName = formData.companyName;
        details.tickerSymbol = formData.tickerSymbol;
        details.exchange = formData.exchange;
        details.sharePrice = formData.sharePrice;
        details.dividendYield = formData.dividendYield;
        details.marketCap = formData.marketCap;
        details.sector = formData.sector;
        break;
      case "Stocks":
        details.companyName = formData.companyName;
        details.tickerSymbol = formData.tickerSymbol;
        details.exchange = formData.exchange;
        details.sharePrice = formData.sharePrice;
        details.dividendYield = formData.dividendYield;
        details.marketCap = formData.marketCap;
        details.sector = formData.sector;
        break;
    }

    return details;
  };

  // Render asset-specific fields based on selected asset type
  const renderAssetSpecificFields = () => {
    const { assetType } = formData;

    if (!assetType) {
      return <div className="errorcss">Please select an asset type first</div>;
    }

    const fields = {
      "Real Estate": [
        {
          label: "Property Type",
          name: "propertyType",
          type: "text",
          required: true,
        },
        {
          label: "Year Built",
          name: "yearBuilt",
          type: "number",
          required: true,
        },
        { label: "Floors", name: "floors", type: "number", required: false },
        {
          label: "Total Area",
          name: "totalArea",
          type: "text",
          required: false,
        },
        {
          label: "Lease Expiry",
          name: "leaseExpiry",
          type: "date",
          required: false,
        },
      ],
      "Treasuries & Bonds": [
        { label: "Issuer", name: "issuer", type: "text", required: true },
        {
          label: "Credit Rating",
          name: "creditRating",
          type: "text",
          required: false,
        },
        {
          label: "Interest Rate",
          name: "interestRate",
          type: "text",
          required: true,
        },
        {
          label: "Maturity Date",
          name: "maturityDate",
          type: "date",
          required: false,
        },
      ],
      "Private Credit": [
        { label: "Issuer", name: "issuer", type: "text", required: true },
        {
          label: "Interest Rate",
          name: "interestRate",
          type: "text",
          required: true,
        },
        {
          label: "Repayment Schedule",
          name: "repaymentSchedule",
          type: "text",
          required: false,
        },
        {
          label: "Collateral Details",
          name: "collateralDetails",
          type: "textarea",
          required: false,
        },
      ],
      Commodities: [
        {
          label: "Commodity Type",
          name: "commodityType",
          type: "text",
          required: true,
        },
        {
          label: "Unit of Measure",
          name: "unitOfMeasure",
          type: "text",
          required: false,
        },
        {
          label: "Storage Location",
          name: "storageLocation",
          type: "text",
          required: false,
        },
        {
          label: "Storage Cost",
          name: "storageCost",
          type: "text",
          required: false,
        },
      ],
      Equities: [
        {
          label: "Company Name",
          name: "companyName",
          type: "text",
          required: true,
        },
        {
          label: "Ticker Symbol",
          name: "tickerSymbol",
          type: "text",
          required: true,
        },
        { label: "Exchange", name: "exchange", type: "text", required: false },
        {
          label: "Share Price",
          name: "sharePrice",
          type: "number",
          required: false,
        },
        {
          label: "Dividend Yield",
          name: "dividendYield",
          type: "text",
          required: false,
        },
        {
          label: "Market Cap",
          name: "marketCap",
          type: "text",
          required: false,
        },
        { label: "Sector", name: "sector", type: "text", required: false },
      ],
      Stocks: [
        {
          label: "Company Name",
          name: "companyName",
          type: "text",
          required: true,
        },
        {
          label: "Ticker Symbol",
          name: "tickerSymbol",
          type: "text",
          required: true,
        },
        { label: "Exchange", name: "exchange", type: "text", required: false },
        {
          label: "Share Price",
          name: "sharePrice",
          type: "number",
          required: false,
        },
        {
          label: "Dividend Yield",
          name: "dividendYield",
          type: "text",
          required: false,
        },
        {
          label: "Market Cap",
          name: "marketCap",
          type: "text",
          required: false,
        },
        { label: "Sector", name: "sector", type: "text", required: false },
      ],
    };

    const currentFields = fields[assetType] || [];

    return (
      <div className="tokeniz_form_inptrow">
        {currentFields.map((field, index) => (
          <div key={index} className="tokeniz_form_inptwrp">
            <label>
              {field.label} {field.required && <span>*</span>}
            </label>
            {field.type === "textarea" ? (
              <textarea
                value={formData[field.name] || ""}
                onChange={handleChange(field.name)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            ) : (
              <input
                type={field.type}
                value={formData[field.name] || ""}
                onChange={handleChange(field.name)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            )}
            {touched[field.name] && errors[field.name] && (
              <div className="errorcss">{errors[field.name]}</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <AdminHeader />
      <main className="main_flx userMang_main">
        <AdminSidebar />
        <div className="right_wrap pb-4">
          <div>
            <h3 className="inspge_title">Create New Asset</h3>
            <div className="tokeniz_card_main">
              <form className="tokeniz_form">
                {/* Progress Steps */}
                <div className="top_step_tap">
                  <ul>
                    {stepCount.map((item, i) => (
                      <li
                        key={i}
                        className={`${
                          currentStep === item.step
                            ? "active_tab"
                            : item.step < currentStep
                            ? "activeTabComp"
                            : ""
                        }`}
                      >
                        <span className="tab_number">{item.step}</span>
                        <span className="tab_tick">
                          <TiTick />
                        </span>
                        <span className="tab_label">{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="tokeniz_detls_wrp">
                  {/* STEP 1: General Details */}
                  {currentStep === 1 && (
                    <div>
                      {/* Image Upload */}
                      <div className="file-upload-main">
                        {formData.profileImage ? (
                          <div className="uploaded-image">
                            <img src={formData.profileImage} alt="Asset" />
                            <button
                              type="button"
                              className="change-image-btn"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  profileImage: "",
                                }))
                              }
                            >
                              Change Image
                            </button>
                          </div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              className="imgMain_upload_input"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleProfileImageUpload(file);
                                }
                              }}
                              disabled={uploadStatus.profile}
                            />
                            <div className="admin_imgUpld">
                              {uploadStatus.profile ? (
                                <LuLoaderCircle className="spinner" />
                              ) : (
                                <>
                                  <img src={admin_imgUpld} alt="Upload" />
                                  <span>300 x 150</span>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {touched.profileImage && errors.profileImage && (
                        <div className="errorcss mt-2">
                          {errors.profileImage}
                        </div>
                      )}

                      <div className="general_input_main">
                        <h3 className="tokeniz_form_title">General Details</h3>
                        <div className="tokeniz_form_inptrow">
                          {/* Asset Name */}
                          <div className="tokeniz_form_inptwrp">
                            <label>
                              Asset Name <span>*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.fundName}
                              onChange={handleChange("fundName")}
                              onBlur={() =>
                                setTouched((prev) => ({
                                  ...prev,
                                  fundName: true,
                                }))
                              }
                              placeholder="Enter Asset Name"
                            />
                            {touched.fundName && errors.fundName && (
                              <div className="errorcss">{errors.fundName}</div>
                            )}
                          </div>

                          {/* Description */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Description</label>
                            <input
                              type="text"
                              value={formData.description}
                              onChange={handleChange("description")}
                              placeholder="Enter Description"
                            />
                          </div>

                          {/* Asset Type Dropdown */}
                          <div className="tokeniz_form_inptwrp admin_select_cmmn">
                            <label>
                              Asset Type <span>*</span>
                            </label>
                            <div className="status_select_main admin_slct_main admin_slct_col">
                              <div className="dropdown">
                                <button
                                  className="btn btn-secondary dropdown-toggle"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                >
                                  <span>
                                    {formData.assetType || "Select Asset Type"}
                                  </span>
                                </button>
                                <ul className="dropdown-menu">
                                  {assetTypeOptions.map((option, idx) => (
                                    <li
                                      key={idx}
                                      onClick={() => handleAssetType(option)}
                                    >
                                      {option}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {touched.assetType && errors.assetType && (
                                <div className="errorcss mt-2">
                                  {errors.assetType}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Asset Class */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Asset Class</label>
                            <input
                              type="text"
                              value={formData.assetClass}
                              onChange={handleChange("assetClass")}
                              placeholder="Enter Asset Class"
                            />
                          </div>

                          {/* Fund Term */}
                          <div className="tokeniz_form_inptwrp admin_select_cmmn">
                            <label>Fund Term</label>
                            <div className="status_select_main admin_slct_main admin_slct_col">
                              <div className="dropdown">
                                <button
                                  className="btn btn-secondary dropdown-toggle"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                >
                                  <span>
                                    {formData.fundTerm || "Select Fund Term"}
                                  </span>
                                </button>
                                <ul className="dropdown-menu">
                                  {fundTermOptions.map((option, idx) => (
                                    <li
                                      key={idx}
                                      onClick={() => handleFundTerm(option)}
                                    >
                                      {option}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Min Investment */}
                          <div className="tokeniz_form_inptwrp">
                            <label>
                              Min Investment <span>*</span>
                            </label>
                            <input
                              type="number"
                              value={formData.minInvestment}
                              onChange={handleChange("minInvestment")}
                              onBlur={() =>
                                setTouched((prev) => ({
                                  ...prev,
                                  minInvestment: true,
                                }))
                              }
                              placeholder="Enter Min Investment"
                              min="0"
                              step="0.01"
                            />
                            {touched.minInvestment && errors.minInvestment && (
                              <div className="errorcss">
                                {errors.minInvestment}
                              </div>
                            )}
                          </div>

                          {/* Token Price */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Token Price</label>
                            <input
                              type="number"
                              value={formData.tokenPrice}
                              onChange={handleChange("tokenPrice")}
                              onBlur={() =>
                                setTouched((prev) => ({
                                  ...prev,
                                  tokenPrice: true,
                                }))
                              }
                              placeholder="Enter Token Price"
                              min="0"
                              step="0.01"
                            />
                            {touched.tokenPrice && errors.tokenPrice && (
                              <div className="errorcss">
                                {errors.tokenPrice}
                              </div>
                            )}
                          </div>

                          {/* Available Tokens */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Available Tokens</label>
                            <input
                              type="number"
                              value={formData.availableTokens}
                              onChange={handleChange("availableTokens")}
                              onBlur={() =>
                                setTouched((prev) => ({
                                  ...prev,
                                  availableTokens: true,
                                }))
                              }
                              placeholder="Enter Available Tokens"
                              min="0"
                            />
                            {touched.availableTokens &&
                              errors.availableTokens && (
                                <div className="errorcss">
                                  {errors.availableTokens}
                                </div>
                              )}
                          </div>

                          {/* Target Returns */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Target Returns</label>
                            <input
                              type="text"
                              value={formData.targetReturns}
                              onChange={handleChange("targetReturns")}
                              onBlur={() =>
                                setTouched((prev) => ({
                                  ...prev,
                                  targetReturns: true,
                                }))
                              }
                              placeholder="e.g., 10% p.a"
                            />
                            {touched.targetReturns && errors.targetReturns && (
                              <div className="errorcss">
                                {errors.targetReturns}
                              </div>
                            )}
                          </div>

                          {/* Geographic Focus */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Geographic Focus</label>
                            <input
                              type="text"
                              value={formData.geographicFocus}
                              onChange={handleChange("geographicFocus")}
                              placeholder="US / EU / Global"
                            />
                          </div>

                          {/* Investment Strategy */}
                          <div className="tokeniz_form_inptwrp">
                            <label>Investment Strategy</label>
                            <input
                              type="text"
                              value={formData.investmentStrategy}
                              onChange={handleChange("investmentStrategy")}
                              placeholder="Residential / ETF / Commodities etc."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Asset-Specific Details */}
                  {currentStep === 2 && (
                    <div className="asset_specific_main">
                      <h3 className="tokeniz_form_title">
                        Asset Specific Details - {formData.assetType}
                      </h3>
                      {renderAssetSpecificFields()}
                    </div>
                  )}

                  {/* STEP 3: Documents */}
                  {currentStep === 3 && (
                    <div className="documents_input_main">
                      <h3 className="tokeniz_form_title">Documents</h3>
                      <div className="tokeniz_form_inptrow">
                        {/* Whitepaper */}
                        <div className="tokeniz_form_inptwrp">
                          <label>
                            Whitepaper / Investment Thesis <span>*</span>
                          </label>
                          <span className="document_upload_main">
                            <MdOutlineFileUpload />
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="documnet_upload_input"
                              onChange={(e) =>
                                handleDocumentUpload(
                                  e.target.files[0],
                                  "whitepaper"
                                )
                              }
                              disabled={uploadStatus.whitepaper}
                            />
                            <span
                              className={
                                formData.whitepaperDoc
                                  ? "docSubmitted"
                                  : "docNotSubmit"
                              }
                            >
                              {uploadStatus.whitepaper
                                ? "Uploading..."
                                : formData.whitepaperDoc
                                ? "Uploaded"
                                : "Choose Doc"}
                            </span>
                          </span>
                          {touched.whitepaperDoc && errors.whitepaperDoc && (
                            <div className="errorcss">
                              {errors.whitepaperDoc}
                            </div>
                          )}
                        </div>

                        {/* Valuation */}
                        <div className="tokeniz_form_inptwrp">
                          <label>
                            Property Valuation Report <span>*</span>
                          </label>
                          <span className="document_upload_main">
                            <MdOutlineFileUpload />
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="documnet_upload_input"
                              onChange={(e) =>
                                handleDocumentUpload(
                                  e.target.files[0],
                                  "valuation"
                                )
                              }
                              disabled={uploadStatus.valuation}
                            />
                            <span
                              className={
                                formData.valuationDoc
                                  ? "docSubmitted"
                                  : "docNotSubmit"
                              }
                            >
                              {uploadStatus.valuation
                                ? "Uploading..."
                                : formData.valuationDoc
                                ? "Uploaded"
                                : "Choose Doc"}
                            </span>
                          </span>
                          {touched.valuationDoc && errors.valuationDoc && (
                            <div className="errorcss">
                              {errors.valuationDoc}
                            </div>
                          )}
                        </div>

                        {/* Legal */}
                        <div className="tokeniz_form_inptwrp">
                          <label>
                            Legal Disclosures <span>*</span>
                          </label>
                          <span className="document_upload_main">
                            <MdOutlineFileUpload />
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="documnet_upload_input"
                              onChange={(e) =>
                                handleDocumentUpload(e.target.files[0], "legal")
                              }
                              disabled={uploadStatus.legal}
                            />
                            <span
                              className={
                                formData.legalDoc
                                  ? "docSubmitted"
                                  : "docNotSubmit"
                              }
                            >
                              {uploadStatus.legal
                                ? "Uploading..."
                                : formData.legalDoc
                                ? "Uploaded"
                                : "Choose Doc"}
                            </span>
                          </span>
                          {touched.legalDoc && errors.legalDoc && (
                            <div className="errorcss">{errors.legalDoc}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: Staking */}
                  {currentStep === 4 && (
                    <div className="staking_input_main">
                      <h3 className="tokeniz_form_title">Staking Details</h3>
                      <div className="tokeniz_form_inptrow">
                        <div className="tokeniz_form_inptwrp">
                          <label>
                            Lock Period <span>*</span>
                          </label>
                          <select
                            value={formData.lockPeriod}
                            onChange={handleChange("lockPeriod")}
                            onBlur={() =>
                              setTouched((prev) => ({
                                ...prev,
                                lockPeriod: true,
                              }))
                            }
                          >
                            <option value="">Select Lock Period</option>
                            {lockOption.map((opt, idx) => (
                              <option key={idx} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          {touched.lockPeriod && errors.lockPeriod && (
                            <div className="errorcss">{errors.lockPeriod}</div>
                          )}
                        </div>

                        <div className="tokeniz_form_inptwrp">
                          <label>
                            APY <span>*</span>
                          </label>
                          <input
                            value={formData.apy}
                            onChange={handleChange("apy")}
                            onBlur={() =>
                              setTouched((prev) => ({ ...prev, apy: true }))
                            }
                            placeholder="e.g. 12% p.a"
                          />
                          {touched.apy && errors.apy && (
                            <div className="errorcss">{errors.apy}</div>
                          )}
                        </div>

                        <div className="tokeniz_form_inptwrp">
                          <label>
                            Min Stake <span>*</span>
                          </label>
                          <input
                            type="number"
                            value={formData.minStake}
                            onChange={handleChange("minStake")}
                            onBlur={() =>
                              setTouched((prev) => ({
                                ...prev,
                                minStake: true,
                              }))
                            }
                            placeholder="e.g., $3,000"
                            min="0"
                            step="0.01"
                          />
                          {touched.minStake && errors.minStake && (
                            <div className="errorcss">{errors.minStake}</div>
                          )}
                        </div>

                        <div className="tokeniz_checkbox_main">
                          <div className="common_chkbox_main">
                            <div className="common_chkbox_wrp">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formData.isReviewed}
                                  onChange={handleChange("isReviewed")}
                                  onBlur={() =>
                                    setTouched((prev) => ({
                                      ...prev,
                                      isReviewed: true,
                                    }))
                                  }
                                />
                              </div>
                              <p>
                                This asset has been reviewed and authorized by
                                the Admin Team.
                              </p>
                            </div>
                            {touched.isReviewed && errors.isReviewed && (
                              <div className="errorcss">
                                {errors.isReviewed}
                              </div>
                            )}
                          </div>

                          <div className="common_chkbox_main">
                            <div className="common_chkbox_wrp">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formData.isConfirmed}
                                  onChange={handleChange("isConfirmed")}
                                  onBlur={() =>
                                    setTouched((prev) => ({
                                      ...prev,
                                      isConfirmed: true,
                                    }))
                                  }
                                />
                              </div>
                              <p>
                                I confirm that all information provided is
                                accurate and complies with listing requirements.
                              </p>
                            </div>
                            {touched.isConfirmed && errors.isConfirmed && (
                              <div className="errorcss">
                                {errors.isConfirmed}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="tab_change_btn">
                  {currentStep === stepCount.length ? (
                    <>
                      <button
                        type="button"
                        className="clear"
                        onClick={handleClear}
                        disabled={isSubmitting}
                      >
                        Clear Form
                      </button>
                      <button
                        type="submit"
                        className="create"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <LuLoaderCircle className="spinner" />
                            Creating Asset...
                          </>
                        ) : (
                          "Create Asset"
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="back"
                        onClick={handleBack}
                        disabled={currentStep === 1 || isSubmitting}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="next"
                        onClick={handleNext}
                        disabled={isSubmitting}
                      >
                        Next
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Tokenization;
